import { useSelector } from 'react-redux';

import { CampaignType } from 'modules/domains/campaign';
import {
  BASE_EXPERIMENT_DETAILS_LIST,
  BASE_EXPERIMENT_DETAILS_LIST_WITH_PROJECTED_END_TIME,
  CONVERSION,
  HOLDOUT_GROUP_OFF_BLAST,
  HOLDOUT_GROUP_ON_BLAST,
  NA,
  RANDOMLY_SPLIT_VARIANTS_OFF_BLAST,
  RANDOMLY_SPLIT_VARIANTS_OFF_TRIGGER,
  UNIQUE_CONVERSION,
  detailsMetadata,
} from 'modules/experiments/constants';
import { getExperimentDetailsCardMetadata } from 'modules/experiments/ducks/selectors';
import { useExperimentsTranslations, useProjectsTimeZone } from 'modules/experiments/hooks';
import {
  BasePageExperimentData,
  BasePageExperimentVariantProperties,
  ExperimentAnalyticsTranslation,
  ExperimentDetailsCardMetadata,
  ExperimentVariantDetails,
  ExperimentVariantResultRecord,
  TakeoverExperimentContextState,
  TestStrategy,
} from 'modules/experiments/types';
import {
  addMinsToFormattedFullDateWithHoursAndMins,
  getFormattedFullDateWithHoursAndMins,
  getTimezoneFromISOZonedDate,
  humanizeMinutesDisplay,
} from 'modules/experiments/utils/date';
import {
  ExperimentState,
  ExperimentType,
  KVAnalyticsMetricResultDefinition,
  MetricDisplayType,
} from 'modules/graphql';
import { Nullable } from 'utils/tsHelpers';
import { UpdateExperimentSettingsFormFieldNames } from '../constants/updateExperimentSettings';
import {
  DEFAULT_ATTRIBUTION_PERIOD_HOURS,
  DEFAULT_HOLDOUT_GROUP_SIZE,
  DEFAULT_MIN_SENDS_PER_VARIANT,
  DEFAULT_TEST_DURATION_HOURS,
  DEFAULT_TEST_DURATION_MINUTES,
  DEFAULT_TEST_SIZE_PERCENTAGE,
} from '../takeover/constants';
import { OriginalExperimentState } from '../takeover/types';
import { highChartifyHistogram } from './charts';
import { printUniqueMetricName } from './common';
import {
  isAnalyticsMetricDoubleResult,
  isAnalyticsMetricTermsResult,
  isAttributionModeLastMessaged,
  isBlastCampaignType,
  isRunningExperiment,
} from './ruleEngine';

type ExperimentDataPairs = {
  [property: string]: string | null;
};

type ExperimentDetailsListArgs = {
  experimentType: ExperimentType;
  experimentState: ExperimentState;
  isBlastCampaign: boolean;
  hasHoldout: boolean;
  hasRandomSplit: boolean;
};

/** Base off of campaignState, experimentType, experimentState, and if exp is randomly split
 *
 * Example states and status are:
 * - Blast campaign, YES Holdout group, Randomly split ON,
 * - Blast campaign, YES Holdout group, Randomly split OFF,
 * - Blast campaign, NO Holdout group, Randomly split ON
 * - Blast campaign, NO Holdout group, Randomly split OFF,
 * - Trigger campaign, Randomly split OFF
 * - Trigger campaign, Randomly split ON
 * - Send Time Optimization / Send Time
 *
 * Returns the ordering array for display
 */
export const getMapFromTypesStatesAndStatuses = ({
  experimentType,
  experimentState,
  isBlastCampaign,
  hasHoldout,
  hasRandomSplit,
}: ExperimentDetailsListArgs) => {
  /* Short circuit rules */
  // ST/STO's don't support randomly split and holdout groups
  if ([ExperimentType.SendTime, ExperimentType.SendTimeOptimization].includes(experimentType)) {
    return BASE_EXPERIMENT_DETAILS_LIST;
  }

  // The base list of items in the experiment details section is going to change
  // For Blast campaign in running state with random variant split turned off
  // As these experiments will have a projected finish time while running.
  const BASE_LIST =
    isBlastCampaign && !hasRandomSplit && experimentState === ExperimentState.RUNNING
      ? BASE_EXPERIMENT_DETAILS_LIST_WITH_PROJECTED_END_TIME
      : BASE_EXPERIMENT_DETAILS_LIST;

  switch (true) {
    // blast
    // - holdouts
    // -- randomly split variants (RSV)
    case isBlastCampaign && hasRandomSplit && hasHoldout:
      return [...BASE_LIST, ...HOLDOUT_GROUP_ON_BLAST];
    // -- no RSV
    case isBlastCampaign && !hasRandomSplit && hasHoldout:
      return [...BASE_LIST, ...RANDOMLY_SPLIT_VARIANTS_OFF_BLAST, ...HOLDOUT_GROUP_ON_BLAST];
    // - no holdouts
    // -- RSV
    case isBlastCampaign && hasRandomSplit && !hasHoldout:
      return [...BASE_LIST, ...HOLDOUT_GROUP_OFF_BLAST];
    // -- no RSV
    case isBlastCampaign && !hasRandomSplit && !hasHoldout:
      return [...BASE_LIST, ...RANDOMLY_SPLIT_VARIANTS_OFF_BLAST, ...HOLDOUT_GROUP_OFF_BLAST];
    // triggered
    case !isBlastCampaign && !hasRandomSplit && !hasHoldout:
      return [...BASE_LIST, ...RANDOMLY_SPLIT_VARIANTS_OFF_TRIGGER];
    case !isBlastCampaign && hasRandomSplit && !hasHoldout:
      return [...BASE_LIST];

    // These states shouldn't be possible.
    // As trigger campaign can't have holdout group
    // But, we have this as a fallback
    case !isBlastCampaign && !hasRandomSplit && hasHoldout:
    case !isBlastCampaign && hasRandomSplit && hasHoldout:
    default:
      return BASE_LIST;
  }
};

type GenerateKeyValuePairsForDisplayType = {
  refinedExperimentDetailsCardMetadataForUI: {
    [property: string]: string | null;
  };
  orderedMapByTypeStateAndStatus: detailsMetadata[];
};

/** Helper function that generates the final key value pairs for display.
 * @param {refinedExperimentDetailsCardMetadataForUI} - refined data mappings
 * @param {orderedMapByTypeStateAndStatus } - array of mapping order
 */
export const generateKeyValuePairsForDisplay = ({
  refinedExperimentDetailsCardMetadataForUI,
  orderedMapByTypeStateAndStatus,
}: GenerateKeyValuePairsForDisplayType): ExperimentDataPairs[] => {
  const mappedValuesForDisplay = orderedMapByTypeStateAndStatus.map((detailsType) => {
    const data = refinedExperimentDetailsCardMetadataForUI[detailsType];
    return { property: detailsType, value: data };
  });
  return mappedValuesForDisplay;
};

/**
 * Experiment test period
 * For blast campaigns, should return the test duration string
 * For trigger campaigns, should return the minimum number of sends per variant
 * @param campaignType
 * @param mins
 * @returns
 */
export const getExperimentTestPeriodByCampaignType = (
  campaignType: CampaignType,
  mins: number,
  testSendsPerVariation: Nullable<number>,
) => {
  const { t } = useExperimentsTranslations();

  if (campaignType === CampaignType.Blast) {
    return humanizeMinutesDisplay(t, mins);
  } else {
    return testSendsPerVariation
      ? `${testSendsPerVariation} ${t(`detailsMetadata.values.sendsPerVariant`)}`
      : '';
  }
};

/**
 * Refines value from the data mapping in a map to displayable string.
 * eg. translates value of initialSendSize: 20 to string '20% of send list'
 */
export const refineExperimentDetailsCardMetadataForUI = ({
  experimentType,
  experimentState,
  experimentTestPeriod,
  experimentStart,
  experimentID,
  createdBy,
  winningCriteria,
  holdout,
  experimentEnd,
  randomlySplitVariants,
  initialSendSize,
  testSendsPerVariation,
  campaignType,
  attributionPeriodHours,
}: ExperimentDetailsCardMetadata): ExperimentDataPairs => {
  const { t } = useExperimentsTranslations();
  const timezone = useProjectsTimeZone();
  const emptyExperimentEndDateText = isRunningExperiment(campaignType, experimentState)
    ? NA
    : t('detailsMetadata.values.notLaunched');

  const refinedDataForUI: {
    [property: string]: string | null;
  } = {
    [detailsMetadata.experimentType]: t(`experimentType.${experimentType}`),
    [detailsMetadata.experimentState]: experimentState as string,
    [detailsMetadata.experimentTestPeriod]: getExperimentTestPeriodByCampaignType(
      campaignType,
      experimentTestPeriod,
      testSendsPerVariation,
    ),
    [detailsMetadata.experimentStart]: experimentStart
      ? getFormattedFullDateWithHoursAndMins(experimentStart, timezone)
      : t('detailsMetadata.values.notLaunched'),
    [detailsMetadata.experimentID]: experimentID ? experimentID.toString() : '',
    [detailsMetadata.createdBy]: createdBy,
    [detailsMetadata.winningCriteria]: winningCriteria || '',
    [detailsMetadata.holdout]:
      holdout && holdout?.percent > 0
        ? t('detailsMetadata.values.holdoutPercent', { holdoutPercent: holdout?.percent })
        : t('bool.no'),
    [detailsMetadata.experimentEnd]: experimentEnd
      ? getFormattedFullDateWithHoursAndMins(experimentEnd || '', timezone)
      : emptyExperimentEndDateText,
    [detailsMetadata.randomlySplitVariants]: randomlySplitVariants
      ? t('detailsMetadata.values.randomlySplitVariants_true')
      : t('detailsMetadata.values.randomlySplitVariants_false'),
    [detailsMetadata.autoSelectWinner]: !randomlySplitVariants ? t('bool.true') : t('bool.false'),
    [detailsMetadata.initialSendSize]: t('detailsMetadata.values.initialSendSize', {
      initialSendSize,
    }),
    [detailsMetadata.projectedExperimentEnd]: addMinsToFormattedFullDateWithHoursAndMins(
      experimentTestPeriod,
      experimentStart,
      timezone,
    ),
    [detailsMetadata.holdoutGroupAttribution]: t('detailsMetadata.values.hours', {
      attributionPeriodHours,
    }),
  };

  return refinedDataForUI;
};

export const getExperimentDetailsMetadata = (): {
  [property: string]: string | null;
}[] => {
  const {
    experimentType,
    experimentState,
    experimentTestPeriod,
    experimentStart,
    experimentID,
    createdBy,
    winningCriteria,
    holdout,
    experimentEnd,
    randomlySplitVariants,
    initialSendSize,
    campaignType,
    testSendsPerVariation,
    attributionPeriodHours,
  } = useSelector(getExperimentDetailsCardMetadata);

  const refinedExperimentDetailsCardMetadataForUI = refineExperimentDetailsCardMetadataForUI({
    experimentType,
    experimentState,
    experimentTestPeriod,
    experimentStart,
    experimentID,
    createdBy,
    winningCriteria,
    holdout,
    experimentEnd,
    randomlySplitVariants,
    initialSendSize,
    campaignType,
    testSendsPerVariation,
    attributionPeriodHours,
  });

  const orderedMapByTypeStateAndStatus = getMapFromTypesStatesAndStatuses({
    experimentType,
    experimentState,
    isBlastCampaign: isBlastCampaignType(campaignType),
    hasHoldout: holdout !== null,
    hasRandomSplit: randomlySplitVariants,
  });

  const experimentDetailsKeyValuePairs = generateKeyValuePairsForDisplay({
    refinedExperimentDetailsCardMetadataForUI,
    orderedMapByTypeStateAndStatus,
  });

  return experimentDetailsKeyValuePairs;
};

export const getVariantDetails = (
  experiment: BasePageExperimentData,
  variant: BasePageExperimentVariantProperties,
): ExperimentVariantDetails => ({
  campaignID: experiment.campaign.id,
  campaignName: experiment.campaign.name,
  variantTemplateID: variant.id,
  variantName: variant.name,
  testSubject: experiment.type,
  content: variant.content,
  templateThumbnailUrl: variant.templateThumbnailUrl,
  control: variant.control,
  winner: variant.winner,
  sendTime: variant.sendTime,
  sendTimeUseRecipientTimeZones: experiment.campaign.sendTimeUseRecipientTimeZones,
  messageMedium: variant.messageMedium,
});

export const getVariantMetrics = (
  metrics: KVAnalyticsMetricResultDefinition[],
  metricDisplayTypeMap: Record<string, MetricDisplayType>,
): ExperimentVariantResultRecord =>
  metrics?.reduce((acc, metric) => {
    if (isAnalyticsMetricDoubleResult(metric)) {
      return {
        ...acc,
        [metric.key.metricName]: {
          value: metric.value.value,
          metricDisplayType: metricDisplayTypeMap[metric.key.metricName],
          histogram: highChartifyHistogram(metric?.value?.histogram?.buckets),
        },
      };
    }
    return acc;
  }, {});

export const getConversionEventMetrics = (
  metrics: KVAnalyticsMetricResultDefinition[],
  translations: ExperimentAnalyticsTranslation,
  isUnique: boolean,
): ExperimentVariantResultRecord =>
  metrics
    ?.filter(isAnalyticsMetricTermsResult)
    .find(({ key: { metricName } }) => metricName === (isUnique ? UNIQUE_CONVERSION : CONVERSION))
    ?.value?.terms?.reduce((acc, { term, value, histogram }) => {
      const eventName = isUnique ? printUniqueMetricName(translations.metrics.unique, term) : term;
      return {
        ...acc,
        [eventName]: {
          value,
          metricDisplayType: 'number',
          histogram: highChartifyHistogram(histogram?.buckets),
        },
      };
    }, {}) || {};

export const getHoldoutMetaInfo = (
  experimentData: OriginalExperimentState,
): TakeoverExperimentContextState['holdoutMetaInfo'] => {
  // attribution
  const customConversionAttribution =
    experimentData?.campaign?.conversions?.[0]?.events?.[0]?.attributionMode;
  const hasAttributionPeriodHours =
    customConversionAttribution && isAttributionModeLastMessaged(customConversionAttribution);
  const customConversionAttributionValue = hasAttributionPeriodHours
    ? customConversionAttribution?.periodHours
    : null;

  const attributionPeriodHours =
    experimentData?.attributionPeriodHours ??
    customConversionAttributionValue ??
    DEFAULT_ATTRIBUTION_PERIOD_HOURS;

  // holdout
  const holdOutGroupSize = experimentData?.holdout?.percent
    ? experimentData.holdout.percent
    : DEFAULT_HOLDOUT_GROUP_SIZE;

  return {
    hasHoldOutGroup: !!experimentData.holdout?.percent,
    holdOutGroupSize,
    attributionPeriodHours,
  };
};

export const getBlastCampaignMetaInfo = (
  experimentData: OriginalExperimentState,
): TakeoverExperimentContextState['blastCampaignMetaInfo'] => {
  // Blast test durations and test size
  const testDurationHours = experimentData?.testDurationInMinutes
    ? Math.floor(experimentData?.testDurationInMinutes / 60)
    : DEFAULT_TEST_DURATION_HOURS;
  const testDurationMinutes = experimentData?.testDurationInMinutes
    ? experimentData?.testDurationInMinutes % 60
    : DEFAULT_TEST_DURATION_MINUTES;
  const testSizePercentage = experimentData?.testSizePercentage
    ? experimentData.testSizePercentage
    : DEFAULT_TEST_SIZE_PERCENTAGE;

  return {
    testStrategy: experimentData.evenlySplitVariations
      ? TestStrategy.ALL_USERS
      : TestStrategy.SUBSET_USERS,
    testDurationHours,
    testDurationMinutes,
    testSizePercentage,
  };
};

export const getTriggerCampaignMetaInfo = (
  experimentData: OriginalExperimentState,
): TakeoverExperimentContextState['triggerCampaignMetaInfo'] => {
  const testSendsPerVariation = experimentData?.testSendsPerVariation
    ? experimentData.testSendsPerVariation
    : DEFAULT_MIN_SENDS_PER_VARIANT;

  return {
    testStrategy: experimentData.evenlySplitVariations
      ? TestStrategy.ALL_USERS
      : TestStrategy.SUBSET_USERS,
    testSendsPerVariation,
  };
};

export const getSendTimeExperimentTimeZone = (
  experimentData: OriginalExperimentState,
  defaultTimeZone: string,
) => {
  const sendTimeStartTimeZone = experimentData.variations?.[0]?.sendTime
    ? getTimezoneFromISOZonedDate(experimentData.variations?.[0]?.sendTime) || defaultTimeZone
    : defaultTimeZone;

  return {
    [UpdateExperimentSettingsFormFieldNames.SEND_TIME_START_TIME_ZONE]: sendTimeStartTimeZone,
    [UpdateExperimentSettingsFormFieldNames.SEND_TIME_USE_RECIPIENT_TIME_ZONE]:
      experimentData.campaign.sendTimeUseRecipientTimeZones,
  };
};
